import { LucideIcon } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

interface ServiceSplitSectionProps {
  icon: LucideIcon;
  title: string;
  benefits: string[];
  imageOnLeft?: boolean;
  background?: "white" | "black";
  number?: string;
  ctaText?: string;
  ctaLink?: string;
  portrait?: string;
  agentName?: string;
  agentRole?: string;
}

const ServiceSplitSection = ({ 
  icon: Icon, 
  title, 
  benefits, 
  imageOnLeft = false,
  background = "white",
  number,
  ctaText,
  ctaLink,
  portrait,
  agentName,
  agentRole,
}: ServiceSplitSectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  
  const bgClass = background === "white" 
    ? "bg-white dark:bg-black" 
    : "bg-black dark:bg-white";
  
  const textColor = background === "white"
    ? "text-black dark:text-white"
    : "text-white dark:text-black";
    
  const secondaryTextColor = background === "white"
    ? "text-gray-600 dark:text-gray-400"
    : "text-gray-300 dark:text-gray-700";

  // Fade-in animation with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Subtle parallax effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      
      const rect = sectionRef.current.getBoundingClientRect();
      const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      
      // Subtle but visible movement: 12% of scroll difference
      const offset = (scrollProgress - 0.5) * 50; // Max ±25px movement
      setParallaxOffset(offset);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const content = (
    <>
      {/* Icon / Portrait Side - Robot suelto sin marco */}
      <div className="flex items-center justify-center py-4 md:py-6">
        {portrait ? (
          <img
            src={portrait}
            alt={agentName ? `${agentName}${agentRole ? ` — ${agentRole}` : ""}` : title}
            loading="lazy"
            width={768}
            height={1024}
            className="w-full max-w-[280px] sm:max-w-[340px] md:max-w-[400px] lg:max-w-[460px] h-auto object-contain animate-robot-float motion-reduce:animate-none"
          />
        ) : (
          <Icon className={`w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 ${textColor} opacity-60`} strokeWidth={0.5} />
        )}
      </div>

      {/* Content Side - Typography Hero */}
      <div className="space-y-6 sm:space-y-8 md:space-y-10 py-8 md:py-12">
        <h3 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light ${textColor} leading-tight tracking-tight`}>
          {title}
        </h3>
        
        <div className="space-y-4 sm:space-y-6">
          {benefits.map((benefit, index) => (
            <p key={index} className={`text-base sm:text-lg md:text-xl lg:text-2xl font-light ${secondaryTextColor} leading-relaxed`}>
              {benefit}
            </p>
          ))}
        </div>

        {ctaText && ctaLink && (
          <div className="pt-4">
            <a 
              href={ctaLink}
              className={`group inline-flex items-center gap-3 text-lg md:text-xl font-medium ${textColor} hover:opacity-70 transition-opacity`}
            >
              <span>{ctaText}</span>
              <span className="text-xl md:text-2xl group-hover:translate-x-2 transition-transform">→</span>
            </a>
          </div>
        )}
      </div>
    </>
  );

  return (
    <section 
      ref={sectionRef}
      className={`${bgClass} py-12 sm:py-16 md:py-20 lg:py-24`} 
      data-service-section 
      data-background={background}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 md:px-12 lg:px-16">
        {/* Número grande con fade-in suave + parallax sutil — se oculta si hay retrato (el número ya está en la ilustración) */}
        {number && !portrait && (
          <div 
            className={`absolute ${parseInt(number) % 2 === 0 ? 'right-0 md:right-8 lg:right-12' : 'left-0 md:left-8 lg:left-12'} top-1/2 text-[6rem] sm:text-[9rem] md:text-[12rem] lg:text-[16rem] font-thin ${textColor} select-none pointer-events-none leading-none`}
            style={{
              opacity: isVisible ? 0.18 : 0,
              transform: `translateY(calc(-50% + ${parallaxOffset}px))`,
              transition: 'opacity 0.8s ease-out'
            }}
          >
            {number}
          </div>
        )}
        <div className={`relative grid md:grid-cols-2 gap-12 md:gap-16 lg:gap-20 items-center ${imageOnLeft ? '' : 'md:[&>*:first-child]:order-2'}`}>
          {content}
          {/* Línea divisoria vertical sutil entre icono y contenido */}
          <div 
            className={`hidden md:block absolute left-1/2 top-[10%] bottom-[10%] w-px -translate-x-1/2 ${
              background === "white" 
                ? "bg-black/[0.15] dark:bg-white/[0.15]" 
                : "bg-white/[0.15] dark:bg-black/[0.15]"
            }`}
          />
        </div>
      </div>
    </section>
  );
};

export default ServiceSplitSection;
